import {
  ArrowLeftIcon,
  CheckCircleIcon,
  PaperPlaneTiltIcon,
  PencilSimpleIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';

import { ApproverApproveDialog } from '@/components/approver/ApproverApproveDialog';
import { ApproverRejectDialog } from '@/components/approver/ApproverRejectDialog';
import { CheckerContextPanel } from '@/components/approver/CheckerContextPanel';
import { EntryHistoryTimeline } from '@/components/approver/EntryHistoryTimeline';
import { ApproveDialog as CheckerApproveDialog } from '@/components/checker/ApproveDialog';
import { RejectDialog as CheckerRejectDialog } from '@/components/checker/RejectDialog';
import { DocumentUploadZone } from '@/components/logsheets-maker/DocumentUploadZone';
import { RejectionBanner } from '@/components/logsheets-maker/RejectionBanner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useLogsheetDetailViewQuery } from '@/hooks/useLogsheetDetailViewQuery';
import { ApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { toast } from '@/lib/toast';
import { formatMachineName } from '@/lib/utils/machine-label';
import {
  deleteLogsheetMakerRequest,
  submitLogsheetMakerRequest,
} from '@/services/logsheets-maker';
import type { LogsheetStatus } from '@/types/logsheets-maker';

function statusVariant(s: LogsheetStatus): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (s === 'Rejected') return 'destructive';
  if (s === 'Approved') return 'default';
  if (s === 'Checked') return 'default';
  if (s === 'Submitted') return 'secondary';
  return 'outline';
}

/**
 * Phase 26 — unified read-only logsheet detail page.
 *
 * Mounted at `/logsheets/view/:id`. Reachable from the Maker list, the Checker queue,
 * the Approver queue, and the Monthly drill-through (`MachineMonthlyEntriesPage`).
 * Replaces the three legacy role-specific detail/review pages
 * (`MakerLogsheetDetailPage`, `CheckerReviewPage`, `ApproverReviewPage`); the legacy
 * routes redirect here.
 *
 * Action eligibility (Edit / Submit / Delete / Approve / Reject) is server-computed
 * via `viewerActions` on the response — the client never re-derives the policy.
 */
export function LogsheetDetailPage(): JSX.Element {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [approveOpen, setApproveOpen] = useState<boolean>(false);
  const [rejectOpen, setRejectOpen] = useState<boolean>(false);

  const detailQuery = useLogsheetDetailViewQuery(id);

  const submitMutation = useMutation<unknown, ApiError>({
    mutationFn: () => submitLogsheetMakerRequest(id),
    onSuccess: () => {
      toast.success('Logsheet submitted');
      void queryClient.invalidateQueries({ queryKey: ['logsheet-view', id] });
      void queryClient.invalidateQueries({ queryKey: ['logsheets-maker'] });
    },
    onError: (err) => {
      toast.error('Could not submit', { description: err.detail ?? err.message });
    },
  });

  const deleteMutation = useMutation<undefined, ApiError>({
    mutationFn: async () => {
      await deleteLogsheetMakerRequest(id);
      return undefined;
    },
    onSuccess: () => {
      toast.success('Logsheet deleted');
      void queryClient.invalidateQueries({ queryKey: ['logsheets-maker'] });
      setDeleteOpen(false);
      void navigate('/logsheets/maker');
    },
    onError: (err) => {
      toast.error('Could not delete', { description: err.detail ?? err.message });
    },
  });

  if (!permissions.includes('logsheet.view')) {
    return <Navigate to="/dashboard" replace />;
  }
  if (!Number.isFinite(id) || id <= 0) {
    return <Navigate to="/dashboard" replace />;
  }

  if (detailQuery.isLoading) {
    return (
      <div className="space-y-6" data-testid="logsheet-view-page">
        <p className="text-sm text-muted-foreground" data-testid="logsheet-view-loading">
          Loading logsheet…
        </p>
      </div>
    );
  }

  if (detailQuery.isError) {
    const err = detailQuery.error;
    const isNotFound = err instanceof ApiError && err.status === 404;
    return (
      <div className="space-y-6" data-testid="logsheet-view-page">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              if (window.history.length > 1) {
                void navigate(-1);
              } else {
                void navigate('/dashboard');
              }
            }}
          >
            <ArrowLeftIcon size={16} weight="bold" /> Back
          </Button>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <p
              className="text-sm text-muted-foreground"
              data-testid={isNotFound ? 'logsheet-view-notfound' : 'logsheet-view-error'}
            >
              {isNotFound
                ? "Logsheet not found or you don't have access to it."
                : 'Failed to load logsheet.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const entry = detailQuery.data;
  if (entry === undefined) {
    return <div data-testid="logsheet-view-page" />;
  }

  const a = entry.viewerActions;
  const showAnyAction = a.canEdit || a.canSubmit || a.canDelete || a.canApprove || a.canReject;

  return (
    <div className="space-y-6" data-testid="logsheet-view-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Logsheet #{id}</h1>
          <p className="text-muted-foreground">
            {formatMachineName(entry.machineName, entry.vehicleNumber, entry.machineCode)} at{' '}
            {entry.siteName} on {entry.entryDate}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={statusVariant(entry.status)} data-testid="logsheet-view-status">
            {entry.status}
          </Badge>
          {a.canEdit && (
            <Button asChild variant="outline" data-testid="logsheet-view-edit">
              <Link to={`/logsheets/maker/${String(id)}/edit`}>
                <PencilSimpleIcon size={16} weight="bold" /> Edit
              </Link>
            </Button>
          )}
          {a.canSubmit && (
            <Button
              type="button"
              onClick={() => {
                submitMutation.mutate();
              }}
              disabled={submitMutation.isPending}
              data-testid="logsheet-view-submit"
            >
              <PaperPlaneTiltIcon size={16} weight="bold" />{' '}
              {submitMutation.isPending ? 'Submitting…' : 'Submit'}
            </Button>
          )}
          {a.canDelete && (
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                setDeleteOpen(true);
              }}
              data-testid="logsheet-view-delete"
            >
              <TrashIcon size={16} weight="bold" /> Delete
            </Button>
          )}
          {a.canApprove && (
            <Button
              type="button"
              onClick={() => {
                setApproveOpen(true);
              }}
              data-testid="logsheet-view-approve"
            >
              <CheckCircleIcon size={16} weight="bold" /> Approve
            </Button>
          )}
          {a.canReject && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setRejectOpen(true);
              }}
              data-testid="logsheet-view-reject"
            >
              Reject
            </Button>
          )}
        </div>
      </div>

      {entry.status === 'Rejected' && entry.rejectionReason !== null && (
        <RejectionBanner
          rejectionReason={entry.rejectionReason}
          rejectedByUserName={entry.rejectedByUserName}
          rejectedAt={entry.rejectedAt}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Summary</CardTitle>
          <CardDescription>
            {showAnyAction
              ? 'Read-only view. Use the action buttons above to make changes.'
              : 'Read-only view.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 text-sm md:grid-cols-3">
          <div>
            <div className="text-muted-foreground text-xs">Site</div>
            <div data-testid="logsheet-view-site">{entry.siteName}</div>
          </div>
          <div>
            <div className="text-muted-foreground text-xs">Machine</div>
            <div data-testid="logsheet-view-machine">
              {formatMachineName(entry.machineName, entry.vehicleNumber, entry.machineCode)}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground text-xs">Entry type</div>
            <div data-testid="logsheet-view-type">{entry.type}</div>
          </div>
          <div>
            <div className="text-muted-foreground text-xs">Date</div>
            <div className="font-mono text-xs">{entry.entryDate}</div>
          </div>
          <div>
            <div className="text-muted-foreground text-xs">Shift</div>
            <div>{entry.shift ?? '—'}</div>
          </div>
          <div>
            <div className="text-muted-foreground text-xs">Maker</div>
            <div>{entry.makerUserName}</div>
          </div>
          <div className="md:col-span-3">
            <div className="text-muted-foreground text-xs">
              {entry.type === 'Work' ? 'Work description' : 'Idle reason'}
            </div>
            <div className="whitespace-pre-wrap" data-testid="logsheet-view-description">
              {entry.type === 'Work'
                ? (entry.workDescription ?? '—')
                : (entry.idleReason ?? '—')}
            </div>
          </div>
          {entry.notes !== null && entry.notes !== '' && (
            <div className="md:col-span-3">
              <div className="text-muted-foreground text-xs">Notes</div>
              <div className="whitespace-pre-wrap">{entry.notes}</div>
            </div>
          )}
        </CardContent>
      </Card>

      {entry.type === 'Work' && Object.keys(entry.kpiValues).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>KPI values</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
              {Object.entries(entry.kpiValues).map(([k, v]) => (
                <div key={k} data-testid={`logsheet-view-kpi-${k}`}>
                  <dt className="text-xs text-muted-foreground">{k}</dt>
                  <dd>{v === null ? '—' : String(v)}</dd>
                </div>
              ))}
            </dl>
          </CardContent>
        </Card>
      )}

      {entry.checkerInfo !== null && (
        <Card>
          <CardHeader>
            <CardTitle>Checker context</CardTitle>
          </CardHeader>
          <CardContent>
            <CheckerContextPanel checker={entry.checkerInfo} />
          </CardContent>
        </Card>
      )}

      {entry.history.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Approval history</CardTitle>
            <CardDescription>Maker → Checker → Approver.</CardDescription>
          </CardHeader>
          <CardContent>
            <EntryHistoryTimeline history={entry.history} />
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Documents</CardTitle>
          <CardDescription>
            Attachments uploaded with this entry.
            {a.isOwner && entry.status === 'Draft' ? ' Remove is available on Drafts.' : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DocumentUploadZone
            entryId={entry.id}
            status={entry.status}
            documents={entry.documents}
            onUploadComplete={() => {
              void queryClient.invalidateQueries({ queryKey: ['logsheet-view', id] });
            }}
            disabled={!(a.isOwner && entry.status === 'Draft')}
          />
        </CardContent>
      </Card>

      {entry.status === 'Approved' && (
        <p
          className="text-xs text-muted-foreground flex items-center gap-1"
          data-testid="logsheet-view-approved-note"
        >
          <CheckCircleIcon size={14} weight="duotone" /> Entry approved — read-only.
        </p>
      )}

      {/* ── Action dialogs ────────────────────────────────────────────── */}

      {a.canDelete && (
        <AlertDialog
          open={deleteOpen}
          onOpenChange={(open) => {
            setDeleteOpen(open);
          }}
        >
          <AlertDialogContent data-testid="logsheet-view-delete-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this Draft?</AlertDialogTitle>
              <AlertDialogDescription>
                This permanently removes the draft and any uploaded documents. This cannot be
                undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  deleteMutation.mutate();
                }}
                disabled={deleteMutation.isPending}
                data-testid="logsheet-view-delete-confirm-button"
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {/*
       * Dialog mount is driven by the entry's workflow status, not the actor's role
       * label. Submitted entries are in the Checker stage; Checked entries are in the
       * Approver stage. This decoupling lets users with multi-role membership (e.g.
       * Super Admin, or a future "Senior Checker" role granted approver permissions)
       * see the correct dialog automatically — the backend already gated `canApprove`
       * / `canReject` on the stage-specific permission, so reaching this point means
       * the actor is authorised to act on whichever stage the entry is currently in.
       */}
      {a.canApprove && entry.status === 'Checked' && (
        <ApproverApproveDialog
          open={approveOpen}
          entryId={id}
          onClose={() => {
            setApproveOpen(false);
          }}
          onSuccess={() => {
            setApproveOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['logsheet-view', id] });
          }}
        />
      )}

      {a.canApprove && entry.status === 'Submitted' && (
        <CheckerApproveDialog
          open={approveOpen}
          entryId={id}
          onClose={() => {
            setApproveOpen(false);
          }}
          onSuccess={() => {
            setApproveOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['logsheet-view', id] });
          }}
        />
      )}

      {a.canReject && entry.status === 'Checked' && (
        <ApproverRejectDialog
          open={rejectOpen}
          entryId={id}
          onClose={() => {
            setRejectOpen(false);
          }}
          onSuccess={() => {
            setRejectOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['logsheet-view', id] });
          }}
        />
      )}

      {a.canReject && entry.status === 'Submitted' && (
        <CheckerRejectDialog
          open={rejectOpen}
          entryId={id}
          onClose={() => {
            setRejectOpen(false);
          }}
          onSuccess={() => {
            setRejectOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['logsheet-view', id] });
          }}
        />
      )}
    </div>
  );
}
